import * as time_utils from "../utility/time_utils";
import { fetchOrchidAPI } from "utils/api";
import getIntervalsInRange from "components/Dashboard/DoctorDashboard/views/one-one-sessions/components/layout/components/calendar-view/utils/getIntervalsInRange";

// make sure we return the same object as long as there is no change in date, so react won't rerender everything
let todayCachedDate: Date | null = null;
export function today() {
  const now = new Date();
  const t = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (todayCachedDate === null || todayCachedDate.getTime() !== t.getTime())
    todayCachedDate = t;
  return todayCachedDate;
}

export function schedulingToIntervalsForDay(scheduling, day) {
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const todayLocal = {
    start: new Date(day.getTime()),
    end: new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1),
  };

  const intervals = scheduling.reduce(
    (accu, current) =>
      accu.concat(
        getIntervalsInRange(
          current,
          +todayLocal.start,
          +todayLocal.end,
          browserTimezone,
        ),
      ),
    [],
  );

  const intervalsOnSpecifiedDay = intervals
    .map((interval) => ({
      start: new Date(interval.start_time),
      end: new Date(interval.end_time),
    }))
    .filter((interval) =>
      time_utils.datetime_interval_intersect(interval, todayLocal),
    );
  return intervalsOnSpecifiedDay.map((interval) => {
    return {
      start:
        interval.start < todayLocal.start ? todayLocal.start : interval.start,
      end: interval.end > todayLocal.end ? todayLocal.end : interval.end,
    };
  });
}

export function scheduleStartEndBusy(scheduling, day) {
  const intervals = schedulingToIntervalsForDay(scheduling, day);

  if (intervals.length === 0) return [null, []];

  const intervalsSorted = intervals.slice(0).sort((a, b) => a.start - b.start);

  const busyTimes: typeof intervalsSorted[] = [];
  let prev: typeof intervalsSorted = null;
  for (const s of intervalsSorted) {
    if (prev) {
      if (prev.start > prev.end)
        throw new Error(
          `overlapping schedule: ${JSON.stringify(intervalsSorted)}`,
        );
      busyTimes.push({ start: prev.end, end: s.start });
    }
    prev = s;
  }

  return [
    {
      start: intervalsSorted[0].start,
      end: intervalsSorted[intervalsSorted.length - 1].end,
    },
    busyTimes,
  ];
}

export function calculateEndTime(appointmentType, start) {
  let endTime = new Date(start.getTime());
  endTime.setMinutes(
    endTime.getMinutes() +
      appointmentType.length_min +
      appointmentType.padding_min,
  );
  return endTime;
}

export function getFreeSlots(appointmentType, busyTimes, dayWorkInterval) {
  let currentStartTime = new Date(dayWorkInterval.start.getTime());
  // fix alignment
  currentStartTime.setMinutes(
    Math.ceil(currentStartTime.getMinutes() / appointmentType.align_min) *
      appointmentType.align_min,
  );

  let currentEndTime = calculateEndTime(appointmentType, currentStartTime);

  const freeSlots: { free: boolean; start?: Date; length_min?: number }[] = [];
  while (currentEndTime <= dayWorkInterval.end) {
    const currentInterval = {
      start: currentStartTime,
      end: currentEndTime,
    };
    if (
      !time_utils.datetime_interval_intersect_any(currentInterval, busyTimes)
    ) {
      freeSlots.push({
        free: true,
        start: new Date(currentStartTime.getTime()),
        length_min: appointmentType.length_min,
      });
    } else {
      if (freeSlots.length > 0 && freeSlots[freeSlots.length - 1].free) {
        freeSlots.push({ free: false });
      }
    }
    currentStartTime.setMinutes(
      currentStartTime.getMinutes() + appointmentType.align_min,
    );
    currentEndTime = calculateEndTime(appointmentType, currentStartTime);
  }
  return freeSlots;
}

export function getFreeSlotsForDay(
  schedule,
  orchidProBusyTimes,
  day,
  appointmentType,
) {
  // block time outside of schedule as busy time
  const [dayWorkInterval, schedulingBusyTimes] = scheduleStartEndBusy(
    schedule,
    day,
  );
  if (!dayWorkInterval) return [];

  // get relevant booked busy times
  const busyTimes = orchidProBusyTimes.map((interval) => {
    return {
      start: new Date(interval.start),
      end: new Date(interval.end),
    };
  });
  const relevantBusyTimes = busyTimes
    .concat(schedulingBusyTimes)
    .filter((t) => time_utils.datetime_interval_intersect(t, dayWorkInterval));

  // calculate free slots
  return getFreeSlots(appointmentType, relevantBusyTimes, dayWorkInterval);
}

export async function getGeneralAvailabilities(
  orchidProCognitoSub: string,
  fetchOptions?: RequestInit,
) {
  // get general availabilities
  const result = await fetchOrchidAPI(
    `/api/scheduler/v1/orchid_pro/available_time_periods/${orchidProCognitoSub}`,
    fetchOptions,
  );
  if (!result.ok) {
    console.error(result.statusText, result.text());
    throw new Error(
      "failed to get general availabilities, please refresh the page",
    );
  }
  return result.json();
}

export async function getBusyTimes(
  orchidProCognitoSub: string,
  fetchOptions?: RequestInit,
) {
  // get general availabilities
  const now = new Date();
  const end = new Date();
  end.setDate(end.getDate() + 360); // TODO: this should do paging!!!
  const result = await fetchOrchidAPI(
    `/api/scheduler/v1/unavailability/${orchidProCognitoSub}?start=${now.toISOString()}&end=${end.toISOString()}`,
    fetchOptions,
  );
  if (!result.ok) {
    console.error(result.statusText, result.text());
    throw new Error("failed to get busy times");
  }
  return result.json();
}

export function getTimeslotInfo(slot, appointmentType) {
  const end = new Date(slot.start.getTime());
  end.setMinutes(end.getMinutes() + slot.length_min);
  const timeslot: AppointmentToBook = {
    timeslot_datetime_start: slot.start,
    timeslot_length_minute: slot.length_min,
    appointmentType: appointmentType,
    price: appointmentType.price,
  };
  return timeslot;
}

export const getStillAvailableSlotsForDay = (
  schedule,
  busyTimes,
  date,
  appointmentType,
) => {
  const freeSlots = getFreeSlotsForDay(
    schedule,
    busyTimes,
    date,
    appointmentType,
  );
  const startingSoon = new Date();
  startingSoon.setMinutes(startingSoon.getMinutes() + 5);
  const relevantFreeSlots = freeSlots.filter(
    (slot) => (slot.start || 0) > startingSoon,
  );
  return relevantFreeSlots;
};
